/**
 * Таблица задач — основной рабочий экран.
 *
 * Компоновка по docs/04 §6.4: сохранённые представления вкладками наверху,
 * тулбар, полоса быстрого отбора, виртуализированная таблица, панель массовых
 * действий и статус-строка вместо пагинации.
 *
 * Отбор двухуровневый. Повседневное — статус, срок, свои задачи — набирается
 * переключателями в одно нажатие: ради «покажи мои в работе» открывать
 * конструктор правил незачем. Конструктор остаётся для сложного и живёт за
 * последним переключателем в той же полосе, чтобы отбор был в одном месте.
 *
 * Пагинации нет и не будет: бесконечная прокрутка курсором и виртуализация —
 * в DOM остаётся три-четыре десятка строк при любом размере выборки.
 */

import * as React from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { Download, Filter, Plus, RefreshCcw, Star, X } from "lucide-react";

import { BoardView, type BoardColumnSpec } from "@/modules/views/BoardView";
import { EMPTY_FILTER, FilterEditor, usable, type FilterDraft } from "@/modules/views/FilterEditor";
import * as api from "@/shared/api/tasks";
import * as views from "@/shared/api/views";
import * as work from "@/shared/api/work";
import { cn } from "@/shared/lib/cn";
import { counted } from "@/shared/lib/plural";
import { useSession } from "@/shared/lib/session";
import { Badge, StatusChip, statusDot, type StatusKind } from "@/shared/ui/Badge";
import { Button, IconButton } from "@/shared/ui/Button";
import { Checkbox } from "@/shared/ui/Choice";
import { DataGrid } from "@/shared/ui/DataGrid";
import { EmptyState, ErrorState } from "@/shared/ui/Feedback";
import { Dialog, DropdownMenu } from "@/shared/ui/Overlays";
import { Select } from "@/shared/ui/Select";
import { TextField } from "@/shared/ui/TextField";
import { useToast } from "@/shared/ui/Toast";

const PAGE = 50;
/** Как часто спрашивать о ходе массовой операции. */
const POLL = 1000;
const SIZING_KEY = "tasks:columns";

export interface TasksPageProps {
  filters: api.TaskFilters;
  onFiltersChange: (filters: api.TaskFilters) => void;
  onOpenTask: (taskId: number) => void;
  /** Открытое представление: его отбор выполняет сервер. */
  viewId?: number;
  onViewChange?: (viewId: number | null) => void;
}

function loadSizing(): ColumnSizingState {
  try {
    return JSON.parse(window.localStorage.getItem(SIZING_KEY) ?? "{}") as ColumnSizingState;
  } catch {
    return {};
  }
}

export function TasksPage({
  filters,
  onFiltersChange,
  onOpenTask,
  viewId,
  onViewChange,
}: TasksPageProps): React.ReactElement {
  const client = useQueryClient();
  const toast = useToast();
  const me = useSession((state) => state.user);
  // Высота области прокрутки нужна виртуализации числом. Мерим по месту, а не
  // вычитаем из высоты окна: полоса отбора переносится на вторую строку, когда
  // статусов много, и таблица иначе уезжала бы под нижние блоки
  const gridBox = React.useRef<HTMLDivElement>(null);
  const gridHeight = useBoxHeight(gridBox);
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [sizing, setSizing] = React.useState<ColumnSizingState>(loadSizing);
  const [creating, setCreating] = React.useState(false);
  const [draftName, setDraftName] = React.useState("");
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<FilterDraft>(EMPTY_FILTER);
  // Правила меняли руками: показывать надо их, а не сохранённое представление
  const [dirty, setDirty] = React.useState(false);
  const [saveAs, setSaveAs] = React.useState<string | null>(null);
  const [runId, setRunId] = React.useState<number | null>(null);
  // «Применить ко всем найденным» — обычное требование: выделять мышью
  // тысячу строк никто не станет
  const [applyToAll, setApplyToAll] = React.useState(false);

  const statuses = useQuery({ queryKey: ["statuses"], queryFn: () => work.listStatuses() });
  const people = useQuery({ queryKey: ["people"], queryFn: work.listPeople });
  // Проект, компания и контрагент приходят в строке номерами: названия берём
  // справочниками, по одному запросу на экран, а не на строку
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => work.listProjects(true) });
  const companies = useQuery({
    queryKey: ["companies", "all"],
    queryFn: () => work.listCompanies(true),
  });
  const counterparties = useQuery({
    queryKey: ["counterparties", "all"],
    queryFn: () => work.listCounterparties(undefined, true),
  });
  const saved = useQuery({ queryKey: ["views"], queryFn: views.listViews });

  /**
   * Вкладками показываются только те представления, которые этот экран умеет
   * нарисовать.
   *
   * Планировщик — доска из нескольких списков, и его место в разделе
   * «Планировщик». Здесь он выпадал в таблицу без отбора вообще: вкладка
   * «Мой план» показывала все задачи подряд.
   */
  const tabs = React.useMemo(
    () => (saved.data ?? []).filter((item) => item.mode !== "planner"),
    [saved.data],
  );

  const openView = tabs.find((item) => item.id === viewId) ?? null;

  // Открыли представление — его правила подставляются в редактор: их видно
  // и можно поправить, не разбирая дерево руками
  const openedId = openView?.id ?? null;
  React.useEffect(() => {
    if (openView) {
      setDraft({ conditions: openView.conditions, logic: openView.logic });
      setDirty(false);
    }
    // Правила представления неизменны, пока открыто одно и то же представление
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedId]);

  // Режим показа — свойство представления: доска и таблица отличаются раскладкой,
  // а не отбором, поэтому переключение не трогает условия
  const mode = openView?.mode ?? "table";
  const boardField =
    typeof openView?.settings["column_field"] === "string"
      ? openView.settings["column_field"]
      : "status";
  const boardColumns: BoardColumnSpec[] = React.useMemo(() => {
    if (boardField !== "status") {
      return [];
    }
    const limits = (openView?.settings["wip"] ?? {}) as Record<string, number>;
    return (statuses.data ?? []).map((status) => ({
      value: status.id,
      label: status.name,
      color: status.color,
      ...(limits[String(status.id)] === undefined ? {} : { wip: limits[String(status.id)] }),
    }));
  }, [boardField, openView, statuses.data]);

  const useView = openedId !== null && !dirty;
  // Значение набирают посимвольно: без задержки каждая буква уходила бы запросом
  const settled = usable(useDebounced(draft, 400));
  const search: views.SearchRequest = {
    conditions: settled.conditions,
    logic: settled.logic,
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.trash ? { trash: true } : {}),
  };
  // У открытого представления считать надо по его условиям, а не по черновику:
  // черновик отстаёт на задержку ввода и в первый миг ещё пуст
  const countRequest: views.SearchRequest =
    useView && openView
      ? {
          conditions: openView.conditions,
          logic: openView.logic,
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.trash ? { trash: true } : {}),
        }
      : search;
  const source = useView
    ? (["view", openedId] as const)
    : settled.conditions.length > 0
      ? (["search", search] as const)
      : (["flat", filters] as const);

  const total = useQuery({
    queryKey: ["tasks", "count", source, countRequest],
    queryFn: () =>
      source[0] === "flat"
        ? api.countTasks(filters)
        : views.countSearch(countRequest).then((result) => ({ total: result.count })),
  });

  const list = useInfiniteQuery({
    queryKey: ["tasks", source],
    queryFn: ({ pageParam }) => {
      if (source[0] === "view") {
        return views.viewTasks(source[1], { limit: PAGE, cursor: pageParam ?? null });
      }
      if (source[0] === "search") {
        return views.searchTasks({ ...search, limit: PAGE, cursor: pageParam ?? null });
      }
      return api.listTasks(filters, { limit: PAGE, cursor: pageParam ?? null });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
  });

  /** Открытое представление выгружается как представление: с его колонками. */
  async function exportRows(format: "csv" | "xlsx"): Promise<void> {
    try {
      await (useView ? views.exportView(openedId, format) : api.exportTasks(filters, format));
    } catch (error) {
      toast.show({
        tone: "danger",
        title: "Не выгрузилось",
        description: (error as Error).message,
      });
    }
  }

  const saveView = useMutation({
    mutationFn: (name: string) =>
      views.createView({
        name,
        conditions: draft.conditions,
        logic: draft.logic,
      }),
    onSuccess: (view) => {
      setSaveAs(null);
      setDirty(false);
      void client.invalidateQueries({ queryKey: ["views"] });
      onViewChange?.(view.id);
    },
    onError: (error: Error) =>
      toast.show({ tone: "danger", title: "Вид не сохранён", description: error.message }),
  });

  const updateView = useMutation({
    mutationFn: (view: views.SavedView) =>
      views.updateView(view.id, { conditions: draft.conditions, logic: draft.logic }),
    onSuccess: () => {
      setDirty(false);
      void client.invalidateQueries({ queryKey: ["views"] });
      toast.show({ tone: "success", title: "Представление обновлено" });
    },
    onError: (error: Error) =>
      toast.show({ tone: "danger", title: "Вид не сохранён", description: error.message }),
  });

  React.useEffect(() => {
    window.localStorage.setItem(SIZING_KEY, JSON.stringify(sizing));
  }, [sizing]);

  const rows = React.useMemo(
    () => (list.data?.pages ?? []).flatMap((page) => page.items),
    [list.data],
  );

  // Карты вынесены в useMemo: иначе колонки пересобираются на каждый рендер,
  // а вместе с ними — вся таблица
  const statusMap = React.useMemo(
    () => new Map((statuses.data ?? []).map((status) => [status.id, status])),
    [statuses.data],
  );
  const peopleMap = React.useMemo(
    () => new Map((people.data ?? []).map((person) => [person.id, person])),
    [people.data],
  );
  const projectMap = React.useMemo(
    () => new Map((projects.data ?? []).map((item) => [item.id, item.name])),
    [projects.data],
  );
  const companyMap = React.useMemo(
    () => new Map((companies.data ?? []).map((item) => [item.id, item.name])),
    [companies.data],
  );
  const counterpartyMap = React.useMemo(
    () => new Map((counterparties.data ?? []).map((item) => [item.id, item.name])),
    [counterparties.data],
  );

  const create = useMutation({
    mutationFn: (name: string) =>
      api.createTask({
        name,
        ...(filters.project_id ? { project_id: filters.project_id } : {}),
      }),
    onSuccess: (task) => {
      setDraftName("");
      setCreating(false);
      void client.invalidateQueries({ queryKey: ["tasks"] });
      onOpenTask(task.id);
    },
  });

  const bulk = useMutation({
    mutationFn: (operation: api.BulkOperation) => api.bulk(operation),
    onSuccess: (run) => {
      setSelected(new Set());
      setRunId(run.id);
    },
    onError: (error: Error) =>
      toast.show({ tone: "danger", title: "Операция не запущена", description: error.message }),
  });

  // Прогон идёт фоном: пока он не закончился, состояние опрашивается,
  // потом опрос прекращается сам — refetchInterval возвращает false
  const run = useQuery({
    queryKey: ["bulk", runId],
    queryFn: () => api.bulkProgress(runId ?? 0),
    enabled: runId !== null,
    refetchInterval: (query) => {
      const value = query.state.data;
      return value && api.isRunOver(value) ? false : POLL;
    },
  });

  const cancelRun = useMutation({
    mutationFn: (id: number) => api.bulkCancel(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["bulk", runId] }),
  });

  const runState = run.data;
  const runOver = runState ? api.isRunOver(runState) : false;
  React.useEffect(() => {
    // Список обновляется по ходу дела: задачи меняются частями, а не разом
    if (runState) {
      void client.invalidateQueries({ queryKey: ["tasks"] });
    }
  }, [client, runState]);

  /** Над чем работает массовая операция: выделенные строки или весь отбор. */
  const targets = (): Pick<
    api.BulkOperation,
    "task_ids" | "all_matching" | "conditions" | "logic" | "q" | "trash"
  > =>
    applyToAll
      ? {
          all_matching: true,
          conditions: settled.conditions,
          logic: settled.logic,
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.trash ? { trash: true } : {}),
        }
      : { task_ids: [...selected] };

  const toggleRow = React.useCallback((taskId: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const columns = React.useMemo<ColumnDef<api.TaskRow>[]>(
    () => [
      {
        id: "select",
        size: 32,
        enableResizing: false,
        header: () => <span className="sr-only">Выделение</span>,
        cell: ({ row }) => (
          // Щелчок по строке открывает задачу: выделение должно останавливать
          // всплытие, иначе галочку нельзя поставить — карточка открывается раньше
          <span
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            role="presentation"
          >
            <Checkbox
              checked={selected.has(row.original.id)}
              onCheckedChange={() => toggleRow(row.original.id)}
              aria-label={`Выделить задачу ${row.original.number}`}
            />
          </span>
        ),
      },
      {
        id: "number",
        size: 64,
        header: "№",
        accessorFn: (row) => row.number,
        cell: ({ row }) => (
          <span className="tabular-nums text-[var(--pt-text-tertiary)]">{row.original.number}</span>
        ),
      },
      {
        id: "name",
        // Название ужато ради двух новых колонок: ширину всё равно тянут мышью,
        // и она запоминается
        size: sizing["name"] ?? 270,
        header: "Название",
        cell: ({ row }) => (
          <span className="flex items-center gap-[var(--pt-space-4)]">
            {row.original.unread_count > 0 && (
              <span
                aria-label="Есть непрочитанное"
                className="size-[6px] shrink-0 rounded-full bg-[var(--pt-accent)]"
              />
            )}
            {row.original.is_favorite && (
              <Star
                aria-hidden
                className="size-[var(--pt-icon-sm)] fill-[var(--pt-warning)] text-[var(--pt-warning)]"
              />
            )}
            <span className="truncate">{row.original.name}</span>
          </span>
        ),
      },
      {
        id: "status",
        size: 140,
        header: "Статус",
        cell: ({ row }) => {
          const status = statusMap.get(row.original.status_id);
          return status ? (
            <StatusChip kind={status.kind as StatusKind} color={status.color} label={status.name} />
          ) : (
            "—"
          );
        },
      },
      {
        id: "assignees",
        size: 150,
        header: "Исполнители",
        cell: ({ row }) => (
          <span className="truncate text-[var(--pt-text-secondary)]">
            {row.original.assignees
              .map((id) => peopleMap.get(id)?.full_name ?? `#${id}`)
              .join(", ") || "—"}
          </span>
        ),
      },
      {
        id: "project",
        size: 170,
        header: "Проект",
        cell: ({ row }) => (
          <span className="truncate text-[var(--pt-text-secondary)]">
            {row.original.project_id === null
              ? "—"
              : (projectMap.get(row.original.project_id) ?? `#${String(row.original.project_id)}`)}
          </span>
        ),
      },
      {
        id: "company",
        // Компанию зовут одной буквой: «Р», «М», «Д», «N» — колонке хватает
        // узкой, и она не отнимает место у названия задачи
        size: 92,
        header: "Компания",
        cell: ({ row }) => (
          <span className="truncate text-[var(--pt-text-secondary)]">
            {row.original.company_id === null
              ? "—"
              : (companyMap.get(row.original.company_id) ?? `#${String(row.original.company_id)}`)}
          </span>
        ),
      },
      {
        id: "counterparty",
        size: 170,
        header: "Контрагент",
        cell: ({ row }) => (
          <span className="truncate text-[var(--pt-text-secondary)]">
            {row.original.counterparty_id === null
              ? "—"
              : (counterpartyMap.get(row.original.counterparty_id) ??
                `#${String(row.original.counterparty_id)}`)}
          </span>
        ),
      },
      {
        id: "created_at",
        size: 104,
        // «Создана», а не «Поставлена»: так это поле называется и в карточке,
        // и в отборе — три имени у одной даты дороже, чем точность формулировки
        header: "Создана",
        cell: ({ row }) => (
          <span className="tabular-nums text-[var(--pt-text-secondary)]">
            {new Date(row.original.created_at).toLocaleDateString("ru")}
          </span>
        ),
      },
      {
        id: "end_at",
        size: 104,
        header: "Срок",
        cell: ({ row }) =>
          row.original.end_at ? (
            <span
              className={cn(
                "tabular-nums",
                new Date(row.original.end_at) < new Date() && "text-[var(--pt-danger-text)]",
              )}
            >
              {new Date(row.original.end_at).toLocaleDateString("ru")}
            </span>
          ) : (
            "—"
          ),
      },
      {
        id: "comments",
        size: 56,
        header: "Лента",
        cell: ({ row }) => (
          <span className="tabular-nums text-[var(--pt-text-tertiary)]">
            {row.original.comment_count}
          </span>
        ),
      },
    ],
    [selected, sizing, statusMap, peopleMap, projectMap, companyMap, counterpartyMap, toggleRow],
  );

  const activeChips = describeFilters(filters);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Высота задана минимумом, а не жёстко: представлений может быть
          десяток, и на узком экране они переносятся на вторую строку.
          При жёсткой высоте перенесённое наезжало на строку ниже */}
      <div className="flex min-h-[32px] shrink-0 flex-wrap items-center gap-[var(--pt-space-8)] py-[var(--pt-space-2)]">
        <ViewTab
          active={openedId === null}
          label="Все задачи"
          onSelect={() => {
            onViewChange?.(null);
            setDraft(EMPTY_FILTER);
            setDirty(false);
          }}
        />
        {tabs.map((view) => (
          <ViewTab
            key={view.id}
            active={view.id === openedId}
            label={view.is_default ? `${view.name} ★` : view.name}
            onSelect={() => onViewChange?.(view.id)}
          />
        ))}
        {openView?.can_edit && dirty && (
          <Button
            variant="ghost"
            size="sm"
            loading={updateView.isPending}
            onClick={() => updateView.mutate(openView)}
          >
            Сохранить изменения
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => setSaveAs("")}>
          + Сохранить вид
        </Button>
      </div>

      <div className="flex min-h-[40px] shrink-0 flex-wrap items-center gap-[var(--pt-space-8)]">
        <Button
          size="sm"
          icon={<Plus className="size-[var(--pt-icon)]" />}
          onClick={() => setCreating(true)}
        >
          Создать
        </Button>

        <div className="ml-auto flex items-center gap-[var(--pt-space-4)]">
          <IconButton
            variant="ghost"
            size="sm"
            label="Обновить"
            icon={<RefreshCcw className="size-[var(--pt-icon)]" />}
            onClick={() => void list.refetch()}
          />
          <DropdownMenu
            align="end"
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={<Download className="size-[var(--pt-icon)]" />}
              >
                Выгрузить
              </Button>
            }
            items={[
              {
                key: "xlsx",
                label: "Excel (XLSX)",
                onSelect: () => void exportRows("xlsx"),
              },
              { key: "csv", label: "CSV", onSelect: () => void exportRows("csv") },
            ]}
          />
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-[var(--pt-space-4)] py-[var(--pt-space-6)]">
        <QuickLabel>Статус</QuickLabel>
        {(statuses.data ?? []).map((status) => (
          <QuickChip
            key={status.id}
            active={(filters.status_id ?? []).includes(status.id)}
            onClick={() =>
              onFiltersChange({ ...filters, status_id: toggle(filters.status_id, status.id) })
            }
          >
            {/* Значок статуса сюда не влезает: цвет показывает точка */}
            <span
              aria-hidden
              className="size-[6px] shrink-0 rounded-full"
              style={{ background: statusDot(status.color) }}
            />
            {status.name}
          </QuickChip>
        ))}

        <QuickSeparator />

        <QuickChip
          active={filters.overdue === true}
          onClick={() => onFiltersChange({ ...filters, overdue: !filters.overdue })}
        >
          Просроченные
        </QuickChip>
        {me && (
          <QuickChip
            active={(filters.assignee_id ?? []).includes(me.id)}
            onClick={() =>
              onFiltersChange({ ...filters, assignee_id: toggle(filters.assignee_id, me.id) })
            }
          >
            Мои
          </QuickChip>
        )}
        <QuickChip
          active={filters.favorite === true}
          onClick={() => onFiltersChange({ ...filters, favorite: !filters.favorite })}
        >
          Избранное
        </QuickChip>
        <QuickChip
          active={filters.unread === true}
          onClick={() => onFiltersChange({ ...filters, unread: !filters.unread })}
        >
          Непрочитанные
        </QuickChip>

        <QuickSeparator />

        <QuickChip
          dashed
          active={draft.conditions.length > 0}
          aria-expanded={filterOpen}
          onClick={() => setFilterOpen((open) => !open)}
        >
          <Filter aria-hidden className="size-[var(--pt-icon-sm)]" />
          {draft.conditions.length > 0
            ? counted(draft.conditions.length, "правило", "правила", "правил")
            : "Правило отбора"}
        </QuickChip>

        {quickCount(filters) > 0 && (
          <button
            type="button"
            onClick={() => onFiltersChange(withoutQuick(filters))}
            className="ml-auto text-[var(--pt-fs-sm)] text-[var(--pt-accent-text)] hover:underline"
          >
            Сбросить отбор ({quickCount(filters)})
          </button>
        )}
      </div>

      {filterOpen && (
        <div className="shrink-0 rounded-[var(--pt-radius-3)] border border-[var(--pt-border-default)] bg-[var(--pt-bg-raised)] p-[var(--pt-space-12)]">
          <FilterEditor
            value={draft}
            onChange={(next) => {
              setDraft(next);
              setDirty(true);
            }}
          />
        </div>
      )}

      <Dialog
        open={saveAs !== null}
        onOpenChange={(open) => setSaveAs(open ? (saveAs ?? "") : null)}
        title="Сохранить представление"
        description="Отбор, который сейчас на экране, останется вкладкой наверху."
        footer={
          <>
            <Button variant="ghost" onClick={() => setSaveAs(null)}>
              Отмена
            </Button>
            <Button
              loading={saveView.isPending}
              onClick={() => saveView.mutate((saveAs ?? "").trim() || "Новое представление")}
            >
              Сохранить
            </Button>
          </>
        }
      >
        <TextField
          label="Название"
          autoFocus
          value={saveAs ?? ""}
          onChange={(event) => setSaveAs(event.target.value)}
        />
      </Dialog>

      {activeChips.length > 0 && (
        <div className="flex min-h-[32px] shrink-0 flex-wrap items-center gap-[var(--pt-space-4)]">
          {activeChips.map((chip) => (
            <Badge key={chip.key} tone="info">
              {chip.label}
            </Badge>
          ))}
          <button
            type="button"
            onClick={() => onFiltersChange({})}
            className="text-[var(--pt-fs-sm)] text-[var(--pt-accent-text)] hover:underline"
          >
            Сбросить всё
          </button>
        </div>
      )}

      {creating && (
        <form
          className="flex shrink-0 items-end gap-[var(--pt-space-8)] py-[var(--pt-space-8)]"
          onSubmit={(event) => {
            event.preventDefault();
            if (draftName.trim()) {
              create.mutate(draftName.trim());
            }
          }}
        >
          <TextField
            label="Название задачи"
            autoFocus
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            containerClassName="flex-1"
          />
          <Button type="submit" loading={create.isPending}>
            Создать
          </Button>
          <IconButton
            variant="ghost"
            label="Отменить"
            icon={<X className="size-[var(--pt-icon)]" />}
            onClick={() => setCreating(false)}
          />
        </form>
      )}

      {mode === "kanban" ? (
        <div className="min-h-0 flex-1">
          <BoardView
            filter={settled}
            columnField={boardField}
            columns={boardColumns}
            onOpenTask={onOpenTask}
            {...(filters.q ? { query: filters.q } : {})}
          />
        </div>
      ) : (
        <div ref={gridBox} className="min-h-0 flex-1">
          <DataGrid
            ariaLabel="Задачи"
            data={rows}
            columns={columns}
            height={gridHeight}
            rowAccent={(row) => STATUS_ACCENT[statusMap.get(row.original.status_id)?.kind ?? ""]}
            loading={list.isPending}
            columnSizing={sizing}
            onColumnSizingChange={setSizing}
            onReachEnd={() => {
              if (list.hasNextPage && !list.isFetchingNextPage) {
                void list.fetchNextPage();
              }
            }}
            onRowClick={(row) => onOpenTask(row.original.id)}
            emptyState={
              // Отказ запроса и пустой отбор — разные вещи: «задач нет»
              // на упавшем запросе отправляет человека править фильтры,
              // которые ни при чём
              list.isError ? (
                <ErrorState what="Задачи" error={list.error} onRetry={() => void list.refetch()} />
              ) : (
                <EmptyState
                  title="Задач нет"
                  description="Измените фильтры или создайте первую задачу."
                  action={<Button onClick={() => setCreating(true)}>Создать задачу</Button>}
                />
              )
            }
          />
        </div>
      )}

      {runState && (
        <div className="flex shrink-0 flex-col gap-[var(--pt-space-4)] rounded-[var(--pt-radius-3)] border border-[var(--pt-border-default)] bg-[var(--pt-bg-raised)] px-[var(--pt-space-12)] py-[var(--pt-space-8)]">
          <div className="flex items-center gap-[var(--pt-space-12)]">
            <span className="text-[var(--pt-font-ui)]">
              {runOver
                ? `Готово: изменено ${String(runState.succeeded)} из ${String(runState.total)}`
                : `Идёт изменение: ${String(runState.processed)} из ${String(runState.total)}`}
            </span>
            <div
              role="progressbar"
              aria-label="Ход массовой операции"
              aria-valuemin={0}
              aria-valuemax={runState.total}
              aria-valuenow={runState.processed}
              className="h-[4px] w-[160px] overflow-hidden rounded-[var(--pt-radius-2)] bg-[var(--pt-bg-sunken)]"
            >
              <div
                className="h-full bg-[var(--pt-accent)]"
                style={{
                  width: `${String(
                    runState.total === 0
                      ? 0
                      : Math.round((runState.processed / runState.total) * 100),
                  )}%`,
                }}
              />
            </div>
            {!runOver && (
              <Button
                variant="ghost"
                size="sm"
                loading={cancelRun.isPending}
                onClick={() => cancelRun.mutate(runState.id)}
              >
                Остановить
              </Button>
            )}
            {runOver && (
              <Button variant="ghost" size="sm" onClick={() => setRunId(null)}>
                Скрыть
              </Button>
            )}
          </div>

          {runState.failed > 0 && (
            <details className="text-[var(--pt-fs-sm)] text-[var(--pt-danger-text)]">
              <summary>
                Не удалось изменить {counted(runState.failed, "задачу", "задачи", "задач")}
              </summary>
              <ul className="mt-[var(--pt-space-4)] flex flex-col gap-[var(--pt-space-2)]">
                {runState.errors.map((item) => (
                  <li key={item.task_id}>
                    Задача {item.task_id}: {item.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex h-[40px] shrink-0 items-center gap-[var(--pt-space-8)] rounded-[var(--pt-radius-3)] bg-[var(--pt-bg-raised)] px-[var(--pt-space-8)] shadow-[var(--pt-shadow-2)]">
          <span className="text-[var(--pt-font-ui)]">Выделено {selected.size}</span>
          {total.data !== undefined && total.data.total > selected.size && (
            <Checkbox
              checked={applyToAll}
              onCheckedChange={(checked) => setApplyToAll(checked === true)}
              label={`Ко всем ${counted(total.data.total, "найденной", "найденным", "найденным")}`}
            />
          )}
          <div className="w-[200px]">
            <Select
              ariaLabel="Сменить статус выделенным"
              placeholder="Сменить статус"
              onValueChange={(value) => bulk.mutate({ ...targets(), status_id: Number(value) })}
              options={(statuses.data ?? []).map((status) => ({
                value: String(status.id),
                label: status.name,
              }))}
            />
          </div>
          <Button
            variant="dangerGhost"
            size="sm"
            onClick={() => bulk.mutate({ ...targets(), to_trash: true })}
          >
            В корзину
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            Снять выделение
          </Button>
        </div>
      )}

      <div className="flex h-[24px] shrink-0 items-center gap-[var(--pt-space-8)] text-[var(--pt-fs-xs)] text-[var(--pt-text-tertiary)]">
        <span>{total.data ? counted(total.data.total, "задача", "задачи", "задач") : "…"}</span>
        {selected.size > 0 && <span>выделено {selected.size}</span>}
        <span>показано {rows.length}</span>
      </div>
    </div>
  );
}

/** Цвет засечки статуса слева в строке. Видна только в «Рабочем столе». */
const STATUS_ACCENT: Record<string, string | undefined> = {
  new: "var(--pt-text-tertiary)",
  in_progress: "var(--pt-accent)",
  done: "var(--pt-success)",
  rejected: "var(--pt-danger)",
};

/** Добавить или убрать значение: переключатель работает на оба хода. */
function toggle(values: number[] | undefined, value: number): number[] {
  const current = values ?? [];
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}

/** Сколько условий набрано переключателями: столько и снимет «Сбросить». */
function quickCount(filters: api.TaskFilters): number {
  return (
    (filters.status_id?.length ?? 0) +
    (filters.assignee_id?.length ?? 0) +
    (filters.overdue ? 1 : 0) +
    (filters.favorite ? 1 : 0) +
    (filters.unread ? 1 : 0)
  );
}

/**
 * Снять то, что набрано переключателями.
 *
 * Проект и строка поиска остаются: их задают не здесь — деревом слева и
 * поиском в шапке, — и сбрасывать их заодно значило бы отменять чужое действие.
 */
function withoutQuick(filters: api.TaskFilters): api.TaskFilters {
  const rest = { ...filters };
  delete rest.status_id;
  delete rest.assignee_id;
  delete rest.overdue;
  delete rest.favorite;
  delete rest.unread;
  return rest;
}

/** Высота блока по месту: виртуализации нужен размер числом. */
function useBoxHeight(ref: React.RefObject<HTMLDivElement | null>): number {
  const [height, setHeight] = React.useState(0);
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box !== undefined) {
        setHeight(box.height);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return height;
}

function QuickLabel({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span className="text-[var(--pt-fs-micro)] uppercase tracking-[var(--pt-ls-micro)] text-[var(--pt-text-tertiary)]">
      {children}
    </span>
  );
}

function QuickSeparator(): React.ReactElement {
  return (
    <span
      aria-hidden
      className="mx-[var(--pt-space-4)] h-[16px] w-[1px] bg-[var(--pt-border-default)]"
    />
  );
}

interface QuickChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active: boolean;
  /** Пунктир отличает вход в конструктор от самих условий. */
  dashed?: boolean;
}

function QuickChip({
  active,
  dashed = false,
  className,
  children,
  ...props
}: QuickChipProps): React.ReactElement {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        "inline-flex h-[var(--pt-control-h-sm)] items-center gap-[var(--pt-space-4)]",
        "rounded-[var(--pt-radius-full)] border px-[var(--pt-space-8)]",
        "whitespace-nowrap text-[var(--pt-font-ui)] transition-colors duration-[var(--pt-dur-micro)]",
        "focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)]",
        dashed && "border-dashed",
        active
          ? "border-[var(--pt-accent-border)] bg-[var(--pt-accent-soft)] text-[var(--pt-accent-text)]"
          : "border-[var(--pt-border-default)] text-[var(--pt-text-secondary)] hover:bg-[var(--pt-bg-hover)] hover:text-[var(--pt-text-primary)]",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/** Значение, отстающее от быстро меняющегося на заданную паузу. */
function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = React.useState(value);
  const serialized = JSON.stringify(value);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
    // Сравнивать надо содержимое: объект пересобирается на каждый рендер
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, delay]);
  return settled;
}

function ViewTab({
  active,
  label,
  onSelect,
}: {
  active: boolean;
  label: string;
  onSelect: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-current={active ? "page" : undefined}
      onClick={onSelect}
      className={cn(
        "shrink-0 whitespace-nowrap rounded-[var(--pt-radius-3)] px-[var(--pt-space-8)]",
        "text-[var(--pt-font-ui)]",
        active
          ? "bg-[var(--pt-bg-selected)] text-[var(--pt-text-primary)]"
          : "text-[var(--pt-text-secondary)] hover:bg-[var(--pt-bg-hover)]",
      )}
    >
      {label}
    </button>
  );
}

/**
 * Условия, пришедшие не из полосы отбора.
 *
 * Статус, срок и прочее видно там же, где их включили, — нажатым
 * переключателем; повторять их значками значило бы говорить дважды. Проект и
 * поиск задаются в других местах экрана, и напомнить о них нужно.
 */
function describeFilters(filters: api.TaskFilters): { key: string; label: string }[] {
  const chips: { key: string; label: string }[] = [];
  if (filters.q) {
    chips.push({ key: "q", label: `Поиск: ${filters.q}` });
  }
  if (filters.project_id) {
    chips.push({ key: "project", label: "Проект выбран" });
  }
  if (filters.trash) {
    chips.push({ key: "trash", label: "Корзина" });
  }
  return chips;
}
